import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Supplier, SupplierFormData, BankAccountFormData } from "@/types/supplier";
import { supplierService } from "@/services/supplierService";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface SupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  supplier?: Supplier;
}

const emptyBankAccount: BankAccountFormData = {
  account_number: "",
  account_type: "Ahorro",
  responsible_name: "",
  currency_type: "GTQ",
};

export function SupplierModal({
  isOpen,
  onClose,
  onSuccess,
  supplier,
}: SupplierModalProps) {
  const { company } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<SupplierFormData>({
    name: "",
    rtu_url: "",
    nit: "",
    email: "",
    phone: "",
    address: "",
    is_active: true,
    bank_accounts: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rtuFile, setRtuFile] = useState<File | null>(null);
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        rtu_url: supplier.rtu_url || "",
        nit: supplier.nit || "",
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || "",
        is_active: supplier.is_active ?? true,
        bank_accounts: supplier.bank_accounts?.map((acc) => ({
          account_number: acc.account_number,
          account_type: acc.account_type,
          responsible_name: acc.responsible_name || "",
          currency_type: acc.currency_type || "GTQ",
        })) || [],
      });
    } else {
      setFormData({
        name: "",
        rtu_url: "",
        nit: "",
        email: "",
        phone: "",
        address: "",
        is_active: true,
        bank_accounts: [],
      });
    }
    setErrors({});
    setFileErrors({});
    setRtuFile(null);
  }, [supplier, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name?.trim()) newErrors.name = "El nombre es requerido";

    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Formato de correo inválido";
    }

    if (formData.bank_accounts) {
      formData.bank_accounts.forEach((acc, index) => {
        if (!acc.account_number?.trim()) {
          newErrors[`account_number_${index}`] = "Número requerido";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddBankAccount = () => {
    setFormData((prev) => ({
      ...prev,
      bank_accounts: [...(prev.bank_accounts || []), { ...emptyBankAccount }],
    }));
  };

  const handleRemoveBankAccount = (index: number) => {
    setFormData((prev) => {
      const newAccounts = [...(prev.bank_accounts || [])];
      newAccounts.splice(index, 1);
      return { ...prev, bank_accounts: newAccounts };
    });
  };

  const handleBankAccountChange = (index: number, field: keyof BankAccountFormData, value: string) => {
    setFormData((prev) => {
      const newAccounts = [...(prev.bank_accounts || [])];
      newAccounts[index] = { ...newAccounts[index], [field]: value };
      return { ...prev, bank_accounts: newAccounts };
    });
    // Limpiar error asociado si existe
    if (errors[`${field}_${index}`]) {
      setErrors((prev) => {
        const newErr = { ...prev };
        delete newErr[`${field}_${index}`];
        return newErr;
      });
    }
  };

  const handleRtuFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setRtuFile(null);
      setFileErrors((prev) => ({ ...prev, rtu: "" }));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setRtuFile(null);
      setFileErrors((prev) => ({ ...prev, rtu: "El archivo supera el tamaño máximo de 10MB." }));
      return;
    }

    setRtuFile(file);
    setFileErrors((prev) => ({ ...prev, rtu: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!company?.slug) {
      toast.error("No se encontró el contexto de la empresa");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...formData,
        slug: company.slug,
      };

      let currentId = supplier?.id;

      if (supplier?.id) {
        await supplierService.update(supplier.id, payload);
        toast.success("Proveedor actualizado exitosamente");
      } else {
        const createRes = await supplierService.create(payload);
        currentId = createRes.data?.id;
        toast.success("Proveedor creado exitosamente");
      }

      if (rtuFile && currentId) {
        try {
          const { data: urlData } = await supplierService.getUploadUrl(
            currentId,
            rtuFile.name,
            rtuFile.type,
            { slug: company.slug }
          );
          
          if (urlData?.upload_url && urlData?.file_url) {
            await supplierService.uploadToS3(urlData.upload_url, rtuFile);
            
            await supplierService.update(currentId, {
              slug: company.slug,
              rtu_url: urlData.file_url,
            });
            toast.success("Archivo RTU subido exitosamente");
          }
        } catch (error: any) {
          toast.error(error.message || "No se pudo subir el archivo RTU");
        }
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error al guardar proveedor:", error);
      toast.error(error.message || "Error al guardar el proveedor");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isSubmitting && !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {supplier ? "Editar Proveedor" : "Crear Proveedor"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, name: e.target.value }));
                  if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                }}
                className={errors.name ? "border-red-500" : ""}
                placeholder="Nombre de la empresa/proveedor"
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="nit">NIT</Label>
              <Input
                id="nit"
                value={formData.nit || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, nit: e.target.value }))}
                placeholder="Número de Identificación Tributaria"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rtu-file">Documento RTU</Label>
              <Input
                id="rtu-file"
                type="file"
                onChange={handleRtuFileChange}
                disabled={isSubmitting}
                accept="application/pdf,image/jpeg,image/png"
              />
              {rtuFile && <p className="text-xs text-slate-600">Seleccionado: {rtuFile.name}</p>}
              {fileErrors.rtu && <p className="text-sm text-red-500">{fileErrors.rtu}</p>}
              {supplier?.rtu_url && (
                <p className="text-xs text-slate-500 mt-1">
                  Archivo actual:{" "}
                  <a href={supplier.rtu_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                    Ver documento
                  </a>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ""}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, email: e.target.value }));
                  if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                }}
                className={errors.email ? "border-red-500" : ""}
                placeholder="correo@ejemplo.com"
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={formData.phone || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+502 1234 5678"
              />
            </div>
            
            <div className="space-y-2 flex flex-col justify-center">
              <Label htmlFor="is_active">Estado Activo</Label>
              <div className="flex items-center space-x-2 mt-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, is_active: checked }))
                  }
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Dirección</Label>
            <Textarea
              id="address"
              value={formData.address || ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
              placeholder="Dirección física del proveedor"
              rows={2}
            />
          </div>

          <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Cuentas Bancarias</h3>
              <Button type="button" variant="outline" size="sm" onClick={handleAddBankAccount}>
                <Plus className="h-4 w-4 mr-2" /> Agregar Cuenta
              </Button>
            </div>

            {(!formData.bank_accounts || formData.bank_accounts.length === 0) && (
              <div className="text-center p-6 border rounded-lg bg-slate-50 dark:bg-slate-900/50 border-dashed">
                <p className="text-sm text-slate-500">No hay cuentas bancarias registradas.</p>
              </div>
            )}

            <div className="space-y-4">
              {formData.bank_accounts?.map((acc, index) => (
                <div key={index} className="p-4 border rounded-lg bg-slate-50 dark:bg-slate-900/30 relative grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute -top-3 -right-3 h-6 w-6 rounded-full bg-red-100 hover:bg-red-200 text-red-600"
                    onClick={() => handleRemoveBankAccount(index)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  
                  <div className="space-y-2">
                    <Label>Número de Cuenta <span className="text-red-500">*</span></Label>
                    <Input
                      value={acc.account_number}
                      onChange={(e) => handleBankAccountChange(index, "account_number", e.target.value)}
                      className={errors[`account_number_${index}`] ? "border-red-500" : ""}
                    />
                    {errors[`account_number_${index}`] && (
                      <p className="text-xs text-red-500">{errors[`account_number_${index}`]}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo de Cuenta</Label>
                    <Select
                      value={acc.account_type}
                      onValueChange={(val) => handleBankAccountChange(index, "account_type", val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ahorro">Ahorro</SelectItem>
                        <SelectItem value="Corriente">Corriente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Responsable / A nombre de</Label>
                    <Input
                      value={acc.responsible_name}
                      onChange={(e) => handleBankAccountChange(index, "responsible_name", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Moneda</Label>
                    <Select
                      value={acc.currency_type}
                      onValueChange={(val) => handleBankAccountChange(index, "currency_type", val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GTQ">Quetzales (GTQ)</SelectItem>
                        <SelectItem value="USD">Dólares (USD)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar Proveedor
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
